'use client';

import { c as _c } from "react/compiler-runtime";
import { Typography } from '@mui/material';
import { Microsoft365RestoreWorkload, RestoreJobType, type InventoryResource, type Microsoft365User, type Path, type RestoreJob } from '@repo/api-gw-sdk';
import useSWR from 'swr';
import { parseMicrosoft365TeamsMessageAttributes } from '@/app/(dashboard)/saas-inventory/microsoft-365/microsoft365TeamsAttributes';
import { CopyToClipboardWrapper } from '@/components/buttons/copyToClipboardButton';
import { BasicLink } from '@/components/explorer/fileLink';
import { scopeLabels, type Microsoft365UserRestoreScope } from '@/components/restore/microsoft365UserRestoreScope';
import { useDAL } from '@/contexts/useDAL';
import type { FilterProperty } from '@/types/advanceFilter';
import { isTeamsItem, isWholeUserItem, microsoft365DisplayPath, microsoft365ExplorerHref, microsoft365RestoreItemLabel, microsoft365RestoreSourceLabel, parseMicrosoft365RestoreItem, type Microsoft365RestoreItem } from './microsoft365RestorePath';
import { RestoreValueLines } from './restoreValueLines';

// A Team has at most 1000 channels, so a few pages of its channels folder always reach a restored channel.
const CHANNEL_NAMES_PAGE_SIZE = 200;

// The job view names a user restore's coverage the way the wizard offered it: the whole user, or one workload.
const restoreScope = (workloads: Microsoft365RestoreWorkload[]): Microsoft365UserRestoreScope | undefined => {
  const hasOneDrive = workloads.includes(Microsoft365RestoreWorkload.MICROSOFT_365_RESTORE_WORKLOAD_ONEDRIVE);
  const hasExchange = workloads.includes(Microsoft365RestoreWorkload.MICROSOFT_365_RESTORE_WORKLOAD_MAILBOX);
  if (hasOneDrive && hasExchange) {
    return 'wholeUser';
  }
  if (hasOneDrive) {
    return 'onedrive';
  }
  if (hasExchange) {
    return 'exchange';
  }
  return undefined;
};
const microsoft365RestoreItems = (job: RestoreJob): Microsoft365RestoreItem[] => {
  if (job.restoreType !== RestoreJobType.Microsoft365Restore) {
    return [];
  }
  return (job.partialRestoreData?.files?.files ?? []).map(file => parseMicrosoft365RestoreItem(file.path, file.pathType)).filter((item): item is Microsoft365RestoreItem => !!item);
};
const userLabel = (user: Microsoft365User): string | undefined => user.displayName || user.userPrincipalName || user.mail;

/**
 * Microsoft 365 index rows carry no owner name, so users are named through the tenant's live
 * directory instead, matched on the id the job recorded. The job may address a user by object id or
 * by principal name, so both key the result — lowercase, since a recorded id's casing may differ
 * from the directory's. A user deleted since the restore keeps its raw id.
 */
const useMicrosoft365UserNames = (job: RestoreJob, userIds: (string | undefined)[]): Map<string, string> => {
  const dal = useDAL();
  const ids = [...new Set(userIds.filter((id): id is string => !!id))].sort();
  const {
    data
  } = dal.saasInventory.microsoft365.useUsersByIds(job.resourceDetails?.id ?? '', ids);
  const names = new Map<string, string>();
  for (const user of data?.users ?? []) {
    const label = userLabel(user);
    if (!label) {
      continue;
    }
    for (const key of [user.userId, user.userPrincipalName]) {
      if (key) {
        names.set(key.toLowerCase(), label);
      }
    }
  }
  return names;
};
const userNameOf = (names: Map<string, string>, userId: string | undefined): string | undefined => userId ? names.get(userId.toLowerCase()) : undefined;
interface Microsoft365TeamsNames {
  teams: Map<string, string>;
  channels: Map<string, string>;
}

/**
 * A Teams index path names the Team and the channel by Graph id only. The Team is named through the
 * tenant's discovered entities; a channel is no entity of its own, so its name is read off the snapshot
 * the job restored from, where every stored message carries it. Either keeps its raw id when the name
 * cannot be resolved.
 */
const useMicrosoft365TeamsNames = (job: RestoreJob, items: (Microsoft365RestoreItem | undefined)[]): Microsoft365TeamsNames => {
  const dal = useDAL();
  const resourceId = job.resourceDetails?.id ?? '';
  const snapshotId = job.snapshotDetails?.id ?? '';
  const teamItems = items.filter((item): item is Microsoft365RestoreItem => !!item && isTeamsItem(item));
  const channelItems = teamItems.filter(item_0 => !!item_0.channelId);
  const teamIds = [...new Set(teamItems.map(item_1 => item_1.entityId))].sort();
  const channelIds = [...new Set(channelItems.map(item_2 => item_2.channelId).filter((id): id is string => !!id))].sort();
  const {
    data: teamsData
  } = dal.saasInventory.microsoft365.useTeamsByIds(resourceId, teamIds);
  const channelPaths = channelItems.map(item_3 => `teams/${item_3.entityId}/channels/${item_3.channelId}`).filter((path, index, all) => all.indexOf(path) === index).sort();
  // The name is stamped on the channel's own row, which is listed under the Team's channels folder;
  // nothing is indexed beneath the channel, so each Team's listing is paged until its channels are found.
  const {
    data: channelNames
  } = useSWR<Map<string, string>>(resourceId && snapshotId && channelPaths.length ? ['m365-restore-channel-names', resourceId, snapshotId, ...channelPaths] : null, async () => {
    const names = new Map<string, string>();
    await Promise.all(teamIds.map(async teamId => {
      const prefix = `teams/${teamId}/channels/`;
      const wanted = new Set(channelPaths.filter(path_0 => path_0.startsWith(prefix)).map(path_1 => path_1.slice(prefix.length)));
      let pageToken: string | undefined;
      let queryExecutionId: string | undefined;
      do {
        const page = await dal.explorer.listPath(resourceId, prefix, CHANNEL_NAMES_PAGE_SIZE, snapshotId, pageToken, queryExecutionId);
        for (const record of page.data ?? []) {
          const channelId = record.path.slice(prefix.length).replace(/\/$/, '');
          const name = parseMicrosoft365TeamsMessageAttributes(record.snapshots?.[0]?.attributes).conversationName;
          if (wanted.delete(channelId) && name) {
            names.set(channelId, name);
          }
        }
        pageToken = page.pagination?.nextToken;
        queryExecutionId = page.pagination?.queryExecutionId;
      } while (pageToken && wanted.size > 0);
    }));
    return names;
  });
  const teams = new Map<string, string>();
  for (const team of teamsData?.teams ?? []) {
    if (team.displayName) {
      teams.set(team.teamId, team.displayName);
    }
  }
  const channels = new Map<string, string>();
  for (const channelId_0 of channelIds) {
    const name_0 = channelNames?.get(channelId_0);
    if (name_0) {
      channels.set(channelId_0, name_0);
    }
  }
  return {
    teams,
    channels
  };
};

// The owning entity's name, whichever directory it comes from: a user for user paths, a Team for
// Teams paths. Sites stay by id; the SharePoint rows name them elsewhere.
const entityNameOf = (userNames: Map<string, string>, teamsNames: Microsoft365TeamsNames, item: Microsoft365RestoreItem | undefined): string | undefined => item && isTeamsItem(item) ? teamsNames.teams.get(item.entityId) : userNameOf(userNames, item?.userId);
const channelNameOf = (teamsNames: Microsoft365TeamsNames, item: Microsoft365RestoreItem | undefined): string | undefined => item?.channelId ? teamsNames.channels.get(item.channelId) : undefined;

/** Names a user the job recorded by id; undefined while loading or when the directory no longer has them. */

export const useMicrosoft365UserName = (job, userId) => {
  const $ = _c(5);
  let t0;
  if ($[0] !== userId) {
    t0 = [userId];
    $[0] = userId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = useMicrosoft365UserNames(job, t0);
  let t2;
  if ($[2] !== t1 || $[3] !== userId) {
    t2 = userNameOf(t1, userId);
    $[2] = t1;
    $[3] = userId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};

/**
 * The named paths the rows show, for the copy control — copying the index form would hand back the
 * object ids the panel deliberately resolves away.
 */
export const useMicrosoft365RestorePathsToCopy = (job, files) => {
  const $ = _c(15);
  let items;
  let t0;
  let t1;
  let t2;
  if ($[0] !== files || $[1] !== job) {
    items = files.map(_temp);
    t0 = useMicrosoft365UserNames;
    t1 = job;
    t2 = items.map(_temp2);
    $[0] = files;
    $[1] = job;
    $[2] = items;
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
  } else {
    items = $[2];
    t0 = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const names = t0(t1, t2);
  const teamsNames = useMicrosoft365TeamsNames(job, items);
  let t3;
  if ($[6] !== files || $[7] !== items || $[8] !== names || $[9] !== teamsNames) {
    let t4;
    if ($[11] !== items || $[12] !== names || $[13] !== teamsNames) {
      t4 = (file_0, index) => microsoft365DisplayPath(file_0.path, entityNameOf(names, teamsNames, items[index]), channelNameOf(teamsNames, items[index]));
      $[11] = items;
      $[12] = names;
      $[13] = teamsNames;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    t3 = files.map(t4).join("\n");
    $[6] = files;
    $[7] = items;
    $[8] = names;
    $[9] = teamsNames;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};

/** Links the restored item into the Microsoft 365 explorer, labelled by its display path. */
export const Microsoft365RestorePathLink = t0 => {
  const $ = _c(11);
  const {
    job,
    resource,
    file
  } = t0;
  const item = parseMicrosoft365RestoreItem(file.path, file.pathType);
  const names = useMicrosoft365UserNames(job, [item?.userId]);
  const teamsNames = useMicrosoft365TeamsNames(job, [item]);
  const T0 = CopyToClipboardWrapper;
  const t1 = file.path;
  const T1 = BasicLink;
  const t2 = microsoft365DisplayPath(file.path, entityNameOf(names, teamsNames, item), channelNameOf(teamsNames, item));
  let t3;
  if ($[0] !== resource.id) {
    t3 = microsoft365ExplorerHref(resource.id);
    $[0] = resource.id;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== T1 || $[3] !== resource || $[4] !== t2 || $[5] !== t3) {
    t4 = <T1 entity={resource} label={t2} href={t3} />;
    $[2] = T1;
    $[3] = resource;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== T0 || $[8] !== file.path || $[9] !== t4) {
    t5 = <T0 textToCopy={t1}>{t4}</T0>;
    $[7] = T0;
    $[8] = file.path;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};
export const microsoft365RestoreSource: FilterProperty<RestoreJob> = {
  name: 'microsoft365RestoreSource',
  displayName: 'Restored from',
  group: '',
  field: {
    Component: ({
      entity
    }) => {
      const sourceByEntity = new Map(microsoft365RestoreItems(entity).map(item => [item.entityId, item]));
      const items = [...sourceByEntity.values()];
      const names = useMicrosoft365UserNames(entity, items.map(item => item.userId));
      const teamsNames = useMicrosoft365TeamsNames(entity, items);
      const labels = items.map(item => microsoft365RestoreSourceLabel(item, entityNameOf(names, teamsNames, item)));
      // useFields keeps a row whose Component returned an element, so an empty value has to resolve to
      // null here rather than inside RestoreValueLines.
      if (!labels.length) {
        return null;
      }
      return <RestoreValueLines lines={labels.map((label, index) => <Typography key={`${index}-${label}`}>{label}</Typography>)} textToCopy={labels.join('\n')} />;
    }
  }
};
export const microsoft365RestoreItem: FilterProperty<RestoreJob> = {
  name: 'microsoft365RestoreItem',
  displayName: 'Restored item',
  group: '',
  field: {
    Component: ({
      entity
    }) => {
      // A job from before the workloads were reported carries no coverage either way, so only an
      // explicit `false` reads as partial.
      const isPartialUser = entity.partialRestoreData?.microsoft365User?.everyWorkload === false;
      const items = microsoft365RestoreItems(entity);
      const teamsNames = useMicrosoft365TeamsNames(entity, items);
      const labels = items.map(item => isWholeUserItem(item) && isPartialUser ? 'Partial user' : microsoft365RestoreItemLabel(item, channelNameOf(teamsNames, item)));
      // A multi-user job records one user root per user and they all read the same, so one line
      // stands for all of them; named items keep every occurrence.
      const lines = labels.filter((label, index) => !isWholeUserItem(items[index]) || labels.indexOf(label) === index);
      if (!lines.length) {
        return null;
      }
      return <RestoreValueLines lines={lines.map((label, index) => <Typography key={`${index}-${label}`}>{label}</Typography>)} textToCopy={lines.join('\n')} />;
    }
  }
};
export const microsoft365RestoreScope: FilterProperty<RestoreJob> = {
  name: 'microsoft365RestoreScope',
  displayName: 'Scope',
  group: '',
  field: {
    // Only a user restore records workloads, so on every other Microsoft 365 restore the row is absent.
    Component: ({
      entity
    }) => {
      const scope = restoreScope(entity.partialRestoreData?.microsoft365User?.workloads ?? []);
      return !!scope && <Typography>{scopeLabels[scope]}</Typography>;
    }
  }
};
function _temp(file) {
  return parseMicrosoft365RestoreItem(file.path, file.pathType);
}
function _temp2(item) {
  return item?.userId;
}